import { ModuleConfiguration, ModuleValidation } from "../../store/features/moduleSlice";
import { MODULE_VALIDATION_RULES, MODULAR_HULL_RULES } from "../../constants/module";

export interface ModuleValidationParams {
  moduleConfig: ModuleConfiguration | null;
  currentStep: string;
}


/**
 * Validates module size requirements
 */
export const validateModuleSize = (size: number): string[] => {
  const errors: string[] = [];
  
  if (size < MODULE_VALIDATION_RULES.size.min) {
    errors.push(`Module size must be at least ${MODULE_VALIDATION_RULES.size.min} tons`);
  }
  
  return errors;
};

/**
 * Validates module technology level
 */
export const validateModuleTechLevel = (techLevel: number): string[] => {
  const errors: string[] = [];
  
  if (techLevel < MODULE_VALIDATION_RULES.techLevel.min) {
    errors.push(`Technology level must be at least ${MODULE_VALIDATION_RULES.techLevel.min}`);
  }
  
  return errors;
};

/**
 * Validates module name
 */
export const validateModuleName = (name: string): string[] => {
  const errors: string[] = [];
  
  if (!name || name.trim().length === 0) {
    errors.push("Module name is required");
    return errors;
  }
  
  if (name.length < MODULE_VALIDATION_RULES.name.minLength) {
    errors.push(`Module name must be at least ${MODULE_VALIDATION_RULES.name.minLength} characters`);
  }
  
  if (name.length > MODULE_VALIDATION_RULES.name.maxLength) {
    errors.push(`Module name cannot exceed ${MODULE_VALIDATION_RULES.name.maxLength} characters`);
  }
  
  return errors;
};



/**
 * Validates module systems according to Traveller rules
 */
export const validateModuleSystems = (systems: ModuleConfiguration["systems"]): string[] => {
  const errors: string[] = [];
  
  // Check optional systems count
  if (systems.optionalSystems.length > MODULE_VALIDATION_RULES.systems.maxOptionalSystems) {
    errors.push(`Module cannot have more than ${MODULE_VALIDATION_RULES.systems.maxOptionalSystems} optional systems`);
  }
  
  // Validate individual systems
  systems.optionalSystems.forEach((system, index) => {
    if (!system.id) {
      errors.push(`Optional system ${index + 1} must have an ID`);
    }
    
    if (!system.name) {
      errors.push(`Optional system ${index + 1} must have a name`);
    }
  });
  
  // Check for non-modular systems (these should not be in modules)
  const nonModularSystemsFound = [];
  if (systems.bridge) nonModularSystemsFound.push("Bridge");
  
  if (nonModularSystemsFound.length > 0) {
    errors.push(`Modules cannot contain: ${nonModularSystemsFound.join(", ")}. These must be in the main hull.`);
  }
  
  return errors;
};

/**
 * Validates module calculations
 */
export const validateModuleCalculations = (calculations: ModuleConfiguration["calculations"]): string[] => {
  const errors: string[] = [];
  
  if (calculations.totalWeight < 0) {
    errors.push("Module total weight cannot be negative");
  }
  
  if (calculations.totalCost < 0) {
    errors.push("Module total cost cannot be negative");
  }
  
  if (calculations.remainingTons < 0) {
    errors.push("Module cannot exceed its allocated tonnage");
  }
  
  if (calculations.crewRequirements < 0) {
    errors.push("Module crew requirements cannot be negative");
  }
  
  return errors;
};

/**
 * Validates module step completion
 */
export const validateModuleStep = (
  moduleConfig: ModuleConfiguration | null,
  stepId: string
): string[] => {
  const errors: string[] = [];
  
  if (!moduleConfig) {
    errors.push("Module configuration is required");
    return errors;
  }
  
  switch (stepId) {
    case "module-basics":
      errors.push(...validateModuleName(moduleConfig.name));
      errors.push(...validateModuleSize(moduleConfig.size));
      errors.push(...validateModuleTechLevel(moduleConfig.techLevel));
      break;
      
    case "module-systems":
      errors.push(...validateModuleSystems(moduleConfig.systems));
      break;
      
    case "module-calculations":
      errors.push(...validateModuleCalculations(moduleConfig.calculations));
      break;
      
    case "module-export":
      errors.push(...validateModuleName(moduleConfig.name));
      if (moduleConfig.calculations.remainingTons < 0) {
        errors.push("Module must be within tonnage limits before export");
      }
      break;
  }
  
  return errors;
};

/**
 * Main module validation function
 */
export const validateModule = (params: ModuleValidationParams): ModuleValidation => {
  const { moduleConfig, currentStep } = params;
  
  const errors: string[] = [];
  const warnings: string[] = [];
  
  if (!moduleConfig) {
    return {
      isValid: false,
      errors: ["Module configuration is required"],
      warnings: [],
    };
  }
  
  // Validate current step
  const stepErrors = validateModuleStep(moduleConfig, currentStep);
  errors.push(...stepErrors);
  
  // Add general validation
  errors.push(...validateModuleName(moduleConfig.name));
  errors.push(...validateModuleSize(moduleConfig.size));
  errors.push(...validateModuleTechLevel(moduleConfig.techLevel));
  errors.push(...validateModuleSystems(moduleConfig.systems));
  errors.push(...validateModuleCalculations(moduleConfig.calculations));
  
  // Add warnings for potential issues
  if (moduleConfig.calculations.remainingTons < 5) {
    warnings.push("Module has very little remaining tonnage");
  }
  
  if (moduleConfig.calculations.totalCost > 1000000) {
    warnings.push("Module cost is very high");
  }
  
  // Only warn about missing optional systems if user has progressed to or past the systems step
  // Removed warning about no optional systems configured
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Calculates module cost according to Traveller rules
 * Modules cost Cr25,000 per ton (as per official rules)
 */
export const calculateModuleCost = (size: number): number => {
  return size * MODULAR_HULL_RULES.moduleCostPerTon;
};

/**
 * Calculates modular hull cost increase
 * Hull cost increases by the modular percentage
 */
export const calculateModularHullCost = (baseHullCost: number, modularPercentage: number): number => {
  const multiplier = 1 + (modularPercentage / 100);
  return baseHullCost * multiplier;
};

/**
 * Validates modular hull percentage
 */
export const validateModularPercentage = (percentage: number): string[] => {
  const errors: string[] = [];
  
  if (percentage < 0) {
    errors.push("Modular percentage cannot be negative");
  }
  
  if (percentage > MODULAR_HULL_RULES.maxModularPercentage) {
    errors.push(`Modular percentage cannot exceed ${MODULAR_HULL_RULES.maxModularPercentage}%`);
  }
  
  return errors;
};
